import { useCallback, useMemo } from 'react';
import { css } from '@emotion/css';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Select, Stack, Typography } from '@lyearn/ui';
import { tagsAndUserAutocompleteClasses } from '@/components/Form/components/Conditions/styles';
import { getSelectOptionLabel } from '@/components/Form/components/Select/getOptionLabel';
import { renderLargeOptionLabel } from '@/components/Form/components/Select/optionLabelRenderer';
import { renderLargeSelectValue } from '@/components/Form/components/Select/renderValue';
import SettingsTabs from '@/components/Form/components/VisibilitySettings/components/VisibilitySettingsTabs';
import SwitchWithInfo from '@/components/SwitchWithInfo';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { ResourceReviewTypeEnum, ReviewerConfigInput, ReviewerTypeEnum } from '@/types/schema';

import SelectGroup from '../SelectGroup';
import SelectPeer from '../SelectPeer';
import SelectReviewers from '../SelectReviewers';
import SelectTag from '../SelectTag';
import SelectUser from '../SelectUser';
import { dropdownContainerStyles } from '../SelectUser/styles';
import { getSettingsTabs } from '../utils';
import { picklistStyles } from './styles';

const reviewsPerLearnerInputWidth = css`
  max-width: 302px;
`;

const AI_REVIEW_TOGGLE_CLASSES = {
  container: 'mx-8 mt-24',
};

const LINK_TYPOGRAPHY = { variant: 'label-01' } as const;

function ReviewerSettings({
  reviewType,
  reviewerConfig,
  showError,
  onReviewerConfigChange,
  initialReviewerConfig,
  isEditMode,
  view = 'form',
}: {
  reviewType: ResourceReviewTypeEnum;
  reviewerConfig: ReviewerConfigInput;
  initialReviewerConfig: ReviewerConfigInput;
  showError?: boolean;
  onReviewerConfigChange: (nextValue: ReviewerConfigInput) => void;
  view?: 'modal' | 'form';
  isEditMode: boolean;
}) {
  const { t } = useTranslation();
  const { [FeatureFlags.EnableLyearnAI]: isLyearnAIEnabled } = useFeatureFlags();

  const { tabs, options } = useMemo(() => {
    const settingsTabs = getSettingsTabs(t, reviewType);
    const options = settingsTabs.map(({ id, ...rest }) => ({
      ...rest,
      value: id as ReviewerTypeEnum,
    }));
    // Remove this when the backend supports changing the type from peer @karanraj22 @iamsaumya
    if (initialReviewerConfig.type === ReviewerTypeEnum.Peer) {
      return { tabs: settingsTabs.filter(({ id }) => id === ReviewerTypeEnum.Peer), options };
    }

    return { tabs: settingsTabs, options };
  }, [initialReviewerConfig.type, reviewType, t]);

  const { type } = reviewerConfig;

  const onTabChange = useCallback(
    (val: ReviewerTypeEnum) => onReviewerConfigChange({ ...reviewerConfig, type: val }),
    [onReviewerConfigChange, reviewerConfig],
  );

  const onEnableAiReviewChange = useCallback(
    (value: boolean) => onReviewerConfigChange({ ...reviewerConfig, enableAiReview: value }),
    [onReviewerConfigChange, reviewerConfig],
  );

  const submissionFieldRenderer = (type: ReviewerTypeEnum) => {
    switch (type) {
      case ReviewerTypeEnum.Peer:
        return (
          <SelectPeer
            className={view === 'form' ? cn('w-1/2', reviewsPerLearnerInputWidth) : 'w-full'}
            initialReviewerConfig={initialReviewerConfig}
            isEditMode={isEditMode}
            reviewerConfig={reviewerConfig}
            showError={showError}
            onReviewerConfigChange={onReviewerConfigChange}
          />
        );
      case ReviewerTypeEnum.Specific:
        return (
          <SelectReviewers
            reviewerConfig={reviewerConfig}
            showError={showError}
            view={view}
            onReviewerConfigChange={onReviewerConfigChange}
          />
        );

      default:
        return (
          <SelectUser
            className={view === 'form' ? cn('w-1/2', dropdownContainerStyles) : 'w-full'}
            classes={
              view === 'form'
                ? tagsAndUserAutocompleteClasses
                : { popper: '!max-h-256', listbox: '!max-h-256' }
            }
            reviewerConfig={reviewerConfig}
            showError={showError}
            type={type}
            onReviewerConfigChange={onReviewerConfigChange}
          />
        );
    }
  };

  return (
    <Box>
      <Typography className="ms-8" color="text-text-primary" variant="label-01">
        {t('Exercise:Creator.ReviewerType')}
      </Typography>
      {view === 'form' ? (
        <SettingsTabs
          className="mt-16 grid grid-cols-2 gap-y-16"
          selectedTab={type}
          tabs={tabs}
          onTabChange={(val) => onTabChange(val as ReviewerTypeEnum)}
        />
      ) : (
        <Select
          disableClearable
          className={cn('mt-12', picklistStyles)}
          getOptionLabel={getSelectOptionLabel}
          optionLabelRenderer={renderLargeOptionLabel}
          options={options}
          renderValue={renderLargeSelectValue}
          size="large"
          value={type}
          onChange={onTabChange}
        />
      )}
      <Stack
        alignItems="start"
        className={cn('mt-28', {
          'space-s-12': view === 'form',
          'space-y-24': view === 'modal',
        })}
        direction={view === 'form' ? 'row' : 'col'}>
        <SelectTag
          className={view === 'form' ? 'w-1/2' : 'w-full'}
          reviewerConfig={reviewerConfig}
          showError={showError}
          type={type}
          onReviewerConfigChange={onReviewerConfigChange}
        />
        <SelectGroup
          className={view === 'form' ? 'w-1/2' : 'w-full'}
          reviewerConfig={reviewerConfig}
          showError={showError}
          type={type}
          onReviewerConfigChange={onReviewerConfigChange}
        />
        {submissionFieldRenderer(type)}
      </Stack>
      {isLyearnAIEnabled ? (
        <SwitchWithInfo
          checked={!!reviewerConfig.enableAiReview}
          classes={AI_REVIEW_TOGGLE_CLASSES}
          description={t('Exercise:Creator.EnableAiReviewDescription')}
          label={t('Exercise:Creator.EnableAiReview')}
          onChange={onEnableAiReviewChange}
        />
      ) : null}
    </Box>
  );
}

export default ReviewerSettings;
